// src/components/PresensiCardParticipantItem.js
import React from "react";
import PropTypes from "prop-types";
import { getAvatarPlaceholder, resolveUmatAvatarSync } from "../utils/wkCommon";

/**
 * Supports content-type shaped participant:
 * - pre-reg / event-participant objects that reference either `.umat` or `.external_person`
 *
 * Render rules implemented:
 * - avatar only for umat; external_person uses placeholder
 * - prereg (participant.prereg === true || participant._isPrereg === true) && not present => soft gray background
 * - roleLabel color differs for Peserta vs Petugas
 * - gender differences: thin left + bottom border (blue-ish for male, pink-ish for female)
 * - line 1: umatID | chineseName pinyinName | roleLabel    (omit umatID/chinese/pinyin for external)
 * - line 2: fullName (or nickname); for external use external_person.name
 * - line 3: area.name or fotang_external (CH + Pinyin) or fotangExternalOtherDesc
 *
 * Props:
 *  - participant: content-type shaped object (may contain .umat or .external_person)
 *  - showDonation: bool
 *  - onClick: fn
 */
export default function PresensiCardParticipantItem({
  participant,
  preRegistration = null,
  showDonation = false,
  onClick,
  onDelete,
  className = "",
}) {
  // helper rem
  const rem = (n) => `${n}rem`;

  const isExternal = !!participant?.external_person;
  const isNonUmat = participant?.personType === "NonUmat";
  const nonUmat = getNonUmat(participant);

  // isPresent from data (truthy)
  const isPresent = !!participant?.isPresent;
  const umat = participant?.umat || null;
  const ext = participant?.external_person || null;

  // detect prereg: either explicit prereg or our synthetic _isPrereg
  const isPrereg = !!participant?.prereg || !!participant?._isPrereg;

  // role label
  const roleLabel = participant?.peserta ? "Peserta" : "Petugas";

  // Line 1: for umat: umatID | chineseName pinyinName  (for external: empty)
  let line1Parts = [];
  if (!isExternal && umat) {
    const idPart = umat.umatID ?? umat.kode ?? "-";
    line1Parts.push(idPart);
    if (umat.chineseName) {
      const pinyin = umat.pinyinName ? ` ${umat.pinyinName}` : "";
      line1Parts.push(`${umat.chineseName}${pinyin}`);
    }
  } // external: leave line1Parts empty (spec says external don't need umatID/chinese/pinyin)
  const line1 = line1Parts.join(" | ");

  // Line 2: fullName or nickname or external_person.name
  //const line2 = isExternal ? (ext?.name ?? "-") : (umat?.fullName ?? umat?.nickName ?? "-");
  const line2 = isExternal
    ? (ext?.name ?? "-")
    : isNonUmat
    ? (nonUmat.name || "-")
    : (umat?.fullName ?? umat?.nickName ?? "-");
    
  // Line 3: area.name OR fotang_external.fotangNameCH + pinyin OR fotangExternalOtherDesc OR address
  let line3 = "-";
  if (!isExternal && umat) {
    if (umat.area && umat.area.name) 
        line3 = umat.area.name;
    else line3 = "-";
  } else if (isExternal && ext) {
    if (ext.fotang_external) {
      const ch = ext.fotang_external.fotangNameCH || "";
      const py = ext.fotang_external.fotangNamePinyin ? ` ${ext.fotang_external.fotangNamePinyin}` : "";
      line3 = `${ch}${py}`.trim() || (ext.address ?? "-");
    } else if (ext.fotangExternalOtherDesc) {
      line3 = ext.fotangExternalOtherDesc;
    } else if (ext.address) {
      line3 = ext.address;
    } else {
      line3 = "-";
    }
  }
  else if (isNonUmat) {
    line3 = nonUmat.remarks || "-";
  }

  // Gender-based border colors (left + bottom)
  //const genderRaw = (isExternal ? ext?.gender : umat?.gender) || "";
  const genderRaw = isExternal
                      ? ext?.gender
                      : isNonUmat
                      ? nonUmat.gender
                      : umat?.gender || "";
  const g = genderRaw.toString().toLowerCase();
  const isMale = g.startsWith("乾");
  const isFemale = g.startsWith("坤");
  const leftColor = isMale ? "#bfdbfe" : isFemale ? "#fecaca" : "#e6e9ee";
  const bottomColor = leftColor;

  // avatar: only show umat.avatar for internal umat; external uses placeholder
  // const avatar = resolveUmatAvatarSync(umat) || getAvatarPlaceholder();
  const avatar = (!isExternal && !isNonUmat)
    ? (resolveUmatAvatarSync(umat) || getAvatarPlaceholder())
    : getAvatarPlaceholder(isMale ? "M" : "F");

    // prereg background if prereg true AND not present
  const isPreRegNotPresent = isPrereg && !isPresent;
  const bg = isPreRegNotPresent ? "#f7f7f8" : "#fff";

  // role label styles
  const roleStyle = {
    fontSize: rem(0.85),
    fontWeight: 700,
    padding: `${rem(0.12)} ${rem(0.4)}`,
    borderRadius: rem(0.3),
    color: participant?.peserta ? "#065f46" : "#7c2d12", // peserta green-ish, petugas red-brown
    background: participant?.peserta ? "#ecfdf5" : "#fff7ed",
    border: "1px solid rgba(0,0,0,0.04)",
  };

  // present (Hadir) badge style — visible: white text on green/blue
  const presentStyle = {
    fontSize: rem(0.75),
    fontWeight: 700,
    padding: `${rem(0.12)} ${rem(0.45)}`,
    borderRadius: rem(0.28),
    color: "#ffffff",
    background: participant?.peserta ? "#16a34a" : "#0ea5a0", // peserta: green, petugas: teal
    border: "none",
    marginLeft: rem(0.4),
  };

  // prereg badge style — subtle outline / filled
  const preregStyle = {
    fontSize: rem(0.75),
    fontWeight: 700,
    padding: `${rem(0.12)} ${rem(0.45)}`,
    borderRadius: rem(0.28),
    color: "#676",
    background: "#fff7ed",
    border: "1px solid rgba(0,0,0,0.04)",
    marginLeft: rem(0.35),
  };

  function getNonUmat(participant) {
    const n = participant?.non_umat_pre_registration;

    return {
      name: participant?.nonUmatName || n?.nonUmatName || "-",
      phone: participant?.nonUmatPhoneNo || n?.nonUmatPhoneNo || "-",
      gender: participant?.nonUmatGender || n?.nonUmatGender || "",
      remarks: participant?.nonUmatRemarks || n?.nonUmatRemarks || "",
    };
  }

  return (
    <div
      className={`participant-card ${className}`}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) onClick(e);
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: rem(0.6),
        background: bg,
        borderRadius: rem(0.6),
        padding: rem(0.85),
        border: "1px solid rgba(14,20,30,0.04)",
        borderLeft: `4px solid ${leftColor}`,
        borderBottom: `3px solid ${bottomColor}`,
        cursor: onClick ? "pointer" : "default",
        position: "relative",
      }}
    >
      {/* ===================== TOP BADGE + DONATION ROW ===================== */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: rem(0.5),
          flexWrap: "wrap",
        }}
      >
        {/* ROLE BADGE */}
        <span style={roleStyle}>{roleLabel}</span>

        {/* PERSON TYPE */}
        <span
          style={{
            fontSize: rem(0.75),
            padding: `${rem(0.12)} ${rem(0.45)}`,
            borderRadius: rem(0.28),
            border: "1px solid rgba(0,0,0,0.06)",
            background: "#fafafa",
            fontWeight: 600,
          }}
        >
          {participant?.personType ||
            (participant?._isPrereg && participant.umat
              ? "Internal"
              : participant.external_person
              ? "External"
              : "NonUmat")}
        </span>

        {/* PREREG */}
        {isPrereg && !isPresent && (
          <span style={preregStyle}>Belum Hadir</span>
        )}

        {/* SEQ NO */}
        {participant?.peserta && participant?.seqNo && (
          <span style={{ fontSize: rem(0.85), color: "#374151" }}>
            No: <strong>{participant.seqNo}</strong>
          </span>
        )}

        {/* PETUGAS ROLE DESC */}
        {!participant?.peserta &&
          (participant?.posName ||
            (preRegistration &&
              (preRegistration.petugasRoleDesc ||
                preRegistration.posName))) && (
            <span style={{ fontSize: rem(0.85), color: "#374151" }}>
              {preRegistration?.petugasRoleDesc}
            </span>
          )}

        {/* SPACER */}
        <div style={{ flex: 1 }} />

        {/* DONATION */}
        {showDonation && (
          <div
            style={{
              fontSize: rem(0.9),
              color: "#1e3a8a",
              fontWeight: 700,
            }}
          >
            {Number(participant?.donationAmount || 0).toLocaleString()}
          </div>
        )}
      </div>

      {/* ===================== MAIN CONTENT ROW ===================== */}
      <div style={{ display: "flex", gap: rem(0.9), alignItems: "flex-start" }}>
        {/* AVATAR */}
        <div style={{ width: rem(5), flex: `0 0 ${rem(5)}` }}>
          <img
            src={avatar}
            alt={line2 || "avatar"}
            style={{
              width: rem(5),
              height: rem(5),
              borderRadius: "999px",
              objectFit: "cover",
            }}
          />
        </div>

        {/* TEXT CONTENT */}
        <div style={{ flex: 1, position: "relative" }}>
          {/* DELETE ICON aligned with Line 1 */}
          {onDelete && participant?.documentId && !participant?._isPrereg && (
            <img
              src="/icons/file-delete.png"
              alt="delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(participant);
              }}
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: rem(1.1),
                height: rem(1.1),
                cursor: "pointer",
                opacity: 0.7,
              }}
            />
          )}

          {/* LINE 1 */}
          <div
            style={{
              fontWeight: 800,
              fontSize: rem(1),
              color: "#0b2f4a",
              paddingRight: rem(1.5),
            }}
          >
            {line1 || (isExternal ? "" : "-")}
          </div>

          {/* LINE 2 */}
          <div
            style={{
              marginTop: rem(0.25),
              fontSize: rem(0.95),
              fontWeight: 700,
              color: "#0b2f4a",
            }}
          >
            {line2}
          </div>

          {/* LINE 3 */}
          <div
            style={{
              marginTop: rem(0.25),
              fontSize: rem(0.87),
              color: "#51606a",
            }}
          >
            {line3}
          </div>
        </div>
      </div>
    </div>
  );

}

PresensiCardParticipantItem.propTypes = {
  participant: PropTypes.object.isRequired,
  showDonation: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
};
